/**
 * Krzywa uziarnienia (analiza sitowa) — oś X logarytmiczna [mm], oś Y „przechodzi [%]".
 *
 * Parametry geometrii i wygładzania są przeniesione 1:1 z prototypu GranuFilt (metoda `chart`):
 * zakres osi X 0,01–100 mm, siatka na 13 działkach, krzywa wygładzana splajnem Catmull-Rom
 * (T = 0,5) z klamrowaniem monotonicznym, żeby wygładzanie nie „wyskakiwało" poza sąsiednie
 * punkty pomiarowe. Opcjonalne markery charakterystycznych średnic i pasma frakcji obsypki.
 */
const BOUNDS = [0.063, 0.125, 0.25, 0.5, 1, 2, 4, 8, 16];
const GRID_X = [0.01, 0.063, 0.125, 0.25, 0.5, 1, 2, 4, 8, 16, 31.5, 63, 100];
const X_MIN = 0.01;
const X_MAX = 100;

const numS = (d: number) => String(d).replace('.', ',');

export interface CurveMarker { d: number; label: string; color: string; }
export interface CurveBand { lo: number; hi: number; color: string; textColor: string; label: string; }

interface Props {
  pass: number[];
  markers?: CurveMarker[];
  bands?: CurveBand[];
  width?: number;
  height?: number;
  /** Lewy margines osi — prototyp używa 44 na ekranie i 58 w raporcie. */
  padLeft?: number;
  /** Dolny margines osi — prototyp używa 30 na ekranie i 44 w raporcie. */
  padBottom?: number;
  className?: string;
}

/**
 * Ścieżka krzywej wygładzona jak w prototypie: kontrolki Catmull-Rom przycięte do przedziału
 * [min, max] pary punktów, więc krzywa nigdy nie zawraca ani nie przestrzeliwuje pionowo.
 */
function smoothPath(pts: [number, number][]): string {
  if (!pts.length) return '';
  const T = 0.5;
  let path = `M${pts[0][0].toFixed(1)} ${pts[0][1].toFixed(1)} `;
  for (let k = 0; k < pts.length - 1; k++) {
    const p0 = pts[k - 1] || pts[k];
    const p1 = pts[k];
    const p2 = pts[k + 1];
    const p3 = pts[k + 2] || pts[k + 1];
    let c1y = p1[1] + ((p2[1] - p0[1]) / 6) * T;
    let c2y = p2[1] - ((p3[1] - p1[1]) / 6) * T;
    const lo = Math.min(p1[1], p2[1]);
    const hi = Math.max(p1[1], p2[1]);
    c1y = Math.max(lo, Math.min(hi, c1y));
    c2y = Math.max(lo, Math.min(hi, c2y));
    const c1x = p1[0] + ((p2[0] - p0[0]) / 6) * T;
    const c2x = p2[0] - ((p3[0] - p1[0]) / 6) * T;
    path += `C${Math.max(p1[0], Math.min(p2[0], c1x)).toFixed(1)} ${c1y.toFixed(1)} `
      + `${Math.max(p1[0], Math.min(p2[0], c2x)).toFixed(1)} ${c2y.toFixed(1)} `
      + `${p2[0].toFixed(1)} ${p2[1].toFixed(1)} `;
  }
  return path;
}

export function GrainCurveChart({
  pass, markers = [], bands = [], width = 640, height = 380,
  padLeft = 44, padBottom = 30, className,
}: Props) {
  const pl = padLeft, pr = 16, pt = 12, pb = padBottom;
  const lx0 = Math.log10(X_MIN), lx1 = Math.log10(X_MAX);
  const X = (d: number) => pl + ((Math.log10(d) - lx0) / (lx1 - lx0)) * (width - pl - pr);
  const Y = (v: number) => pt + ((100 - v) / 100) * (height - pt - pb);
  const plotBottom = height - pb;

  const path = smoothPath(pass.map((p, k) => [X(BOUNDS[k]), Y(p)] as [number, number]));
  const hasData = pass.some(p => p > 0);

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className={className}
         style={{ width: '100%', height: 'auto', maxWidth: width }} role="img" aria-label="Krzywa uziarnienia">
      {/* pasma frakcji (PN/DVGW) */}
      {bands.map((b, i) => (
        <g key={'b' + i}>
          <rect x={X(b.lo)} y={pt} width={Math.max(0, X(b.hi) - X(b.lo))} height={plotBottom - pt} fill={b.color} />
          <text x={X(b.lo) + 2} y={pt + 8 + i * 11} fontSize="10.5" fontWeight="700" fill={b.textColor}>{b.label}</text>
        </g>
      ))}
      {/* siatka pionowa + etykiety X */}
      {GRID_X.map(d => (
        <g key={'gx' + d}>
          <line x1={X(d)} y1={pt} x2={X(d)} y2={plotBottom} stroke="#e2ebf0" strokeWidth={1} />
          <text x={X(d)} y={plotBottom + 12} fontSize="10" fill="#7b8f9a" textAnchor="middle">{numS(d)}</text>
        </g>
      ))}
      {/* siatka pozioma + etykiety Y */}
      {[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map(v => (
        <g key={'gy' + v}>
          <line x1={pl} y1={Y(v)} x2={width - pr} y2={Y(v)} stroke="#e2ebf0" strokeWidth={1} />
          {v % 20 === 0 && <text x={pl - 6} y={Y(v) + 3} fontSize="10" fill="#7b8f9a" textAnchor="end">{v}</text>}
        </g>
      ))}
      {/* markery charakterystycznych średnic */}
      {markers.filter(m => m.d > X_MIN && m.d < X_MAX).map((m, i) => (
        <g key={'m' + i}>
          <line x1={X(m.d)} y1={pt} x2={X(m.d)} y2={plotBottom} stroke={m.color} strokeWidth={1.5} strokeDasharray="4 3" />
          <text x={X(m.d) + 4} y={pt + 14 + i * 15} fontSize="10.5" fontWeight="700" fill={m.color}>{m.label}</text>
        </g>
      ))}
      {/* krzywa */}
      {hasData && <path d={path} stroke="#1d9be3" strokeWidth={2.5} fill="none" strokeLinejoin="round" />}
      {/* osie opisy */}
      <text x={width - pr} y={height - 2} fontSize="10.5" fill="#7b8f9a" fontWeight="700" textAnchor="end">średnica ziarna d [mm]</text>
      <text x={2} y={pt - 3} fontSize="10.5" fill="#7b8f9a" fontWeight="700">przesiew [%]</text>
    </svg>
  );
}
